import { useEffect, useState, MouseEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type { DateRange } from 'react-day-picker';
import { format } from 'date-fns/format';

import { Ui, Theme, String } from '@silvertours/front-shared';
import { useLocale } from '../Runtime';

import { Button } from './DatePicker';
import { TimePicker } from './TimePicker';
import { addTimeToDate } from './DatePicker/Calendar';

import { Backdrop, StyledCalendar, TimeRange } from './DateRangePicker.styles';

const HOUR_IN_MS = 1000 * 60 * 60;

type DateRangePickerProps = {
  open?: boolean;
  vertical?: boolean;
  size?: 'small';
  error: string;
  onDatesChange?: (dateRange: DateRange) => void;
} & DateRange;

const DateRangePicker = ({
  from,
  to,
  open = false,
  vertical = false,
  size,
  error,
  onDatesChange,
}: DateRangePickerProps) => {
  const t = useTranslations('entities.datePicker');
  const { language } = useLocale();

  const [isCalendarVisible, setIsCalendarVisible] = useState(open);
  const [selectedDates, setSelectedDates] = useState<DateRange>({
    from,
    to,
  });

  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();

  useEffect(() => {
    setSelectedDates({ from, to });
  }, [from, to]);

  const localDateFormats =
    String.dateFormats[language as keyof typeof String.dateFormats];

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsCalendarVisible(false);
  };

  const handleDatesChange = (dateRange: DateRange) => {
    if (typeof onDatesChange !== 'undefined') {
      onDatesChange(dateRange);
    }
    setIsCalendarVisible(isMobileTouchDevice);
  };

  const handleTimeChange = (time: string, type: 'from' | 'to') => {
    const [hours, minutes] = time.split(':');
    if (selectedDates[type]) {
      const dateWithTime = addTimeToDate(
        selectedDates[type]!,
        Number(hours),
        Number(minutes),
      );

      const newDateRange = { ...selectedDates, [type]: dateWithTime };
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange(newDateRange);
      }
      setSelectedDates(newDateRange);
    }
  };

  const handleButtonClick = useCallback(() => {
    setIsCalendarVisible(!isCalendarVisible);
  }, [isCalendarVisible]);

  const getDays = () => {
    if (selectedDates.from && selectedDates.to) {
      // Inspired by https://youmightnotneed.com/date-fns#differenceInDays,
      // because differenceInHours() from date-fns always rounds which is not what we want
      const days =
        ((selectedDates.to as any) - (selectedDates.from as any)) /
        HOUR_IN_MS /
        24;
      return Number.isInteger(days) ? days : Math.ceil(days);
    }
    return 1;
  };

  const dayCountText = t('dayCount', { days: getDays() });

  return (
    <>
      {isCalendarVisible && (
        <Backdrop role="presentation" onClick={handleBackdropClick} />
      )}

      <Button
        title={t('datePicker.label')}
        from={from}
        to={to}
        size={size}
        onClick={handleButtonClick}
      />

      <Ui.ErrorMessage>{error}</Ui.ErrorMessage>

      {isCalendarVisible && (
        <StyledCalendar
          from={from}
          to={to}
          dayCountText={dayCountText}
          closeText={t('close')}
          vertical={vertical}
          showHeader={vertical}
          showFooter
          onChange={handleDatesChange}
          onClose={() => {
            setIsCalendarVisible(false);
          }}
        />
      )}

      <TimeRange>
        <TimePicker
          id="depTime"
          type="from"
          label={t('depTime.label')}
          value={
            selectedDates.from
              ? format(selectedDates.from, localDateFormats.timeGeneric)
              : undefined
          }
          onChange={handleTimeChange}
        />
        <TimePicker
          id="destTime"
          type="to"
          label={t('destTime.label')}
          value={
            selectedDates.to
              ? format(selectedDates.to, localDateFormats.timeGeneric)
              : undefined
          }
          onChange={handleTimeChange}
        />
      </TimeRange>
    </>
  );
};

export { DateRangePicker };
